/**
 * Applications Redux slice
 * Manages application list, single application, and loading states
 * @module features/applications/applicationSlice
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import applicationService from './applicationService';

// ── Async thunks ──────────────────────────────────────────────────────────────

export const fetchApplications = createAsyncThunk(
  'applications/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      return await applicationService.getApplications(params);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch applications');
    }
  }
);

export const fetchApplicationBySlug = createAsyncThunk(
  'applications/fetchBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      return await applicationService.getApplicationBySlug(slug);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Application not found');
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const applicationSlice = createSlice({
  name: 'applications',
  initialState: {
    applications: [],
    application: null,
    pagination: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearApplication: (state) => {
      state.application = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchApplications
      .addCase(fetchApplications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.applications = action.payload.applications;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // fetchApplicationBySlug
      .addCase(fetchApplicationBySlug.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.application = null;
      })
      .addCase(fetchApplicationBySlug.fulfilled, (state, action) => {
        state.isLoading = false;
        state.application = action.payload.application;
      })
      .addCase(fetchApplicationBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApplication } = applicationSlice.actions;
export default applicationSlice.reducer;

// Developed and Designed by Trophy Developers
